// src/components/pages/admin-dash/DrawEdit.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Spinner from "../../ui/Spinner";
import ConfirmDialog from "../../ui/ConfirmDialog";
import DrawForm from "./DrawForm";

/**
 * Edit / delete a single draw. Backend (`DrawAdminResource`) has had
 * PUT/DELETE for a while — this is the UI that was missing
 * (1-ADMIN_ROADMAP.md item 2.1).
 *
 * Reached from DrawsListAll (row action) or DrawGetById ("Edit"/"Delete"
 * buttons), which pass the draw id via onNavigate("editDraw", {drawId}).
 *
 * Props:
 *   drawId          number
 *   onShowToast(message, tone)
 *   onSaved()        called after a successful PUT
 *   onDeleted()      called after a successful DELETE
 */
export default function DrawEdit({ drawId, onShowToast, onSaved, onDeleted }) {
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance.get(`/admin/draw/${drawId}`)
      .then((res) => { if (!cancelled) setDraw(res.data); })
      .catch(() => { if (!cancelled) setError("Draw not found, or failed to load."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [drawId]);

  const initialValue = draw && {
    typeLottery: draw.typeLottery || "MEGA_MILLIONS",
    moonPhase: draw.moonPhase || "",
    date: draw.date?.split?.("T")[0] || draw.date || "",
    value: draw.value ?? "",
    num1: draw.num1 ?? "", num2: draw.num2 ?? "", num3: draw.num3 ?? "",
    num4: draw.num4 ?? "", num5: draw.num5 ?? "",
    extraBall: draw.extraBall ?? "",
  };

  const handleSubmit = async (body) => {
    setSubmitting(true);
    try {
      await axiosInstance.put(`/admin/draw/${drawId}`, body);
      onShowToast?.(`Draw #${drawId} updated.`, "success");
      onSaved?.();
    } catch (err) {
      onShowToast?.(
        err?.response?.data?.message || err?.response?.data?.violations?.[0] || "Failed to update draw.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/draw/${drawId}`);
      onShowToast?.(`Draw #${drawId} deleted.`, "success");
      setConfirmOpen(false);
      onDeleted?.();
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Failed to delete draw.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
            Edit draw #{drawId}
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            Changes are saved directly — there's no undo besides editing again.
          </p>
        </div>
        <Button variant="danger" icon="trash" size="sm" onClick={() => setConfirmOpen(true)} disabled={loading || !draw}>
          Delete draw
        </Button>
      </header>

      {loading && (
        <Card className="!p-6 flex items-center justify-center">
          <Spinner size={18} />
        </Card>
      )}

      {!loading && error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      {!loading && draw && (
        <DrawForm initialValue={initialValue} submitLabel="Save changes" submitting={submitting} onSubmit={handleSubmit} />
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={`Delete draw #${drawId}?`}
        message="This permanently removes the draw record. Any match results tied to it are not affected, but this action can't be undone."
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        icon="trash"
      />
    </div>
  );
}
